/**
 * SplitLines
 *
 * Auto-splits heading (h1–h6) and <p> elements inside `.split-lines` wrappers
 * into individual visual-line spans so each line animates independently.
 *
 * Structure expected (before):
 *   .animation
 *     └── .split-lines.duration-*   ← wrapper (loses split-lines after split)
 *           ├── h2                   ← will be split
 *           └── p                    ← will be split
 *
 * Structure after split:
 *   .animation
 *     └── .duration-*
 *           ├── h2
 *           │    ├── <span class="line animation-line overflow-hidden duration-*">
 *           │    │     <span class="words anima-bottom duration-slow">
 *           │    │       <span class="word">Welcome</span>
 *           │    │       <span class="whitespace"> </span>
 *           │    │       <span class="word">to</span>
 *           │    │     </span>
 *           │    │   </span>
 *           └── p (same structure)
 *
 * Only runs at xl breakpoint (≥ 1280px) to match the `@screen xl` CSS gate.
 * Inline elements (e.g. <a>) are treated as atomic units and never split.
 * Must be called after document.fonts.ready to ensure correct line metrics.
 *
 * Usage: await document.fonts.ready; new SplitLines().split();
 */

const XL_BREAKPOINT = 1280;

export default class SplitLines {
  /**
   * Entry point. Finds all .split-lines wrappers inside .animation,
   * then splits the h1–h6 / p children into per-line spans.
   * @returns {boolean} false if skipped (e.g. below xl)
   */
  split() {
    if (window.innerWidth < XL_BREAKPOINT) return false;

    const wrappers = document.querySelectorAll('.animation .split-lines');

    wrappers.forEach((wrapper) => {
      const targets = wrapper.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
      if (targets.length === 0) return; // no text content — leave wrapper untouched

      // Duration lives on the wrapper (e.g. duration-slower), pass it to each line
      const durationClass = this.extractDurationClass(wrapper.className);

      // Check if wrapper has opacity-0 (should be removed after animation)
      const hasOpacity0 = wrapper.classList.contains('opacity-0');

      // Remove split-lines from the wrapper — individual lines will carry anima-bottom instead
      wrapper.classList.remove('split-lines');

      targets.forEach((el) => {
        if (el.querySelector('a')) {
          // Elements containing links: skip split, use opacity fade only
          el.classList.add('anima-opacity');
          if (durationClass) el.classList.add(durationClass);
        } else {
          this.splitElement(el, durationClass);
        }
      });

      // Remove opacity-0 after animation completes
      if (hasOpacity0) {
        this.removeOpacityAfterAnimation(wrapper);
      }
    });

    return true;
  }

  /**
   * Splits a single element into per-line <span>s.
   * @param {HTMLElement} el
   * @param {string} durationClass — e.g. "duration-slower", inherited from wrapper
   */
  splitElement(el, durationClass) {

    // --- Step 1: Wrap every word / inline element into a temp js-word span ---
    const wordSpans = this.wrapWords(el);
    if (wordSpans.length === 0) return;

    // --- Step 2: Measure vertical position of each word span after render ---
    const lines = this.groupByLine(wordSpans);

    // --- Step 3: Rebuild element content as js-line spans ---
    this.rebuildAsLines(el, lines, durationClass);
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the first "duration-*" class found, or empty string.
   */
  extractDurationClass(className) {
    const match = className.match(/\bduration-[\w-]+\b/);
    return match ? match[0] : '';
  }

  /**
   * Iterates childNodes, wraps text words and inline elements in spans.
   * Text is split into .word and .whitespace spans.
   * Returns the array of created word spans.
   * @param {HTMLElement} el
   * @returns {HTMLElement[]}
   */
  wrapWords(el) {
    // Clone child nodes snapshot (iterating live NodeList while mutating is unsafe)
    const childNodes = Array.from(el.childNodes);
    el.innerHTML = '';

    const wordSpans = [];

    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Split text by space, preserve whitespace
        const words = node.textContent.split(/(\s+)/);
        words.forEach((token) => {
          if (!token) return;
          const span = document.createElement('span');
          const isWhitespace = /^\s+$/.test(token);
          span.className = isWhitespace ? 'whitespace' : 'word';
          span.textContent = token;
          el.appendChild(span);
          wordSpans.push(span);
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Inline elements (a, strong, em, span, etc.) are atomic — wrap as .word
        const span = document.createElement('span');
        span.className = 'word';
        span.appendChild(node);
        el.appendChild(span);
        wordSpans.push(span);
      }
    });

    return wordSpans;
  }

  /**
   * Groups word spans into arrays by visual line (same top offset, ±2px).
   * @param {HTMLElement[]} wordSpans
   * @returns {HTMLElement[][]}
   */
  groupByLine(wordSpans) {
    const lines = [];
    let currentLine = [];
    let currentTop = null;

    wordSpans.forEach((span) => {
      const top = Math.round(span.getBoundingClientRect().top);
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        if (currentLine.length) lines.push(currentLine);
        currentLine = [span];
        currentTop = top;
      } else {
        currentLine.push(span);
      }
    });

    if (currentLine.length) lines.push(currentLine);
    return lines;
  }

  /**
   * Clears the element and rebuilds it with line/words/word structure.
   * Each line: <span class="line animation-line duration-*"><span class="words anima-bottom delay-N">...</span></span>
   * @param {HTMLElement} el
   * @param {HTMLElement[][]} lines
   * @param {string} durationClass
   */
  rebuildAsLines(el, lines, durationClass) {
    el.innerHTML = '';

    lines.forEach((wordSpans) => {
      // Outer line container
      const lineSpan = document.createElement('span');
      lineSpan.className = [
        'line',
        'animation-line overflow-hidden',
        durationClass,
      ]
        .filter(Boolean)
        .join(' ');

      // Inner words wrapper
      const wordsSpan = document.createElement('span');
      wordsSpan.className = [
        'words',
        'anima-bottom',
        'duration-slower',
      ]
        .filter(Boolean)
        .join(' ');

      // Move all word/whitespace spans into words wrapper
      wordSpans.forEach((wordSpan) => {
        wordsSpan.appendChild(wordSpan);
      });

      lineSpan.appendChild(wordsSpan);
      el.appendChild(lineSpan);
    });
  }

  /**
   * Removes opacity-0 class from wrapper after animation completes
   * @param {HTMLElement} wrapper
   */
  removeOpacityAfterAnimation(wrapper) {
    // Find all animated elements (lines with .words)
    const animatedElements = wrapper.querySelectorAll('.words.anima-bottom, .anima-opacity');

    if (animatedElements.length === 0) {
      // No animated elements, remove opacity-0 immediately
      wrapper.classList.remove('opacity-0');
      return;
    }

    let completedCount = 0;
    const totalCount = animatedElements.length;

    const handleAnimationEnd = (e) => {
      // Only count animations from direct children (not nested)
      if (e.target !== e.currentTarget) return;

      completedCount++;

      // Remove opacity-0 when all animations complete
      if (completedCount >= totalCount) {
        wrapper.classList.remove('opacity-0');
      }
    };

    // Listen for animationend on each animated element
    animatedElements.forEach((el) => {
      el.addEventListener('animationend', handleAnimationEnd, { once: true });
    });
  }
}
